import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import NavbarAll from '../Navbar/NavbarAll';
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { getEffectiveHifzUser } from "./hifzAuth";
import HifzBackgroundImage from "../../assets/audio/images/hifz-portal-image1.png";

const dashboardPathForRole = (role) => {
  if (role === "admin") return "/dashboard-admin";
  if (role === "qari") return "/dashboard-qari";
  return "/dashboard-student";
};

// Background wrapper — masjid image with a soft white/green overlay
const Background = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 24px;
  box-sizing: border-box;
  position: relative;
  background: url(${HifzBackgroundImage}) no-repeat center center;
  background-size: cover;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      160deg,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(0, 80, 48, 0.55) 100%
    );
    backdrop-filter: blur(2px);
  }
`;

// Centered white login card
const LoginContainer = styled.div`
  max-width: 400px;
  width: 100%;
  margin: auto;
  padding: 36px 28px 30px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e2ede4;
  border-top: 4px solid #47a447;
  box-shadow: 0 20px 50px rgba(0, 32, 16, 0.28);
  position: relative;
  z-index: 1;
`;

const LoginHeading = styled.h2`
  text-align: center;
  color: #005030;
  margin-bottom: 6px;
  font-size: 1.8rem;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const SubHeading = styled.p`
  text-align: center;
  color: #5f7167;
  font-size: 0.9rem;
  margin: 0 0 24px;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const InputLabel = styled.label`
  margin-bottom: 8px;
  color: #33473c;
  font-size: 0.9rem;
  font-weight: 600;
`;

const InputField = styled.input`
  width: 100%;
  padding: 12px 14px;
  margin-bottom: 18px;
  border: 1px solid ${(p) => (p.$hasError ? "#dc2626" : "#d8e8db")};
  border-radius: 10px;
  outline: none;
  font-size: 1rem;
  color: #1c2b22;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$hasError ? "#dc2626" : "#47a447")};
    box-shadow: 0 0 0 3px
      ${(p) => (p.$hasError ? "rgba(220, 38, 38, 0.18)" : "rgba(71, 164, 71, 0.2)")};
  }

  &:disabled {
    background: #f4faf5;
    cursor: not-allowed;
  }
`;

const PasswordWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 18px;

  input {
    margin-bottom: 0;
    padding-right: 46px;
  }
`;

const EyeButton = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  color: #5f7167;
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s, background 0.2s;

  &:hover {
    color: #005030;
    background: #eef4ef;
  }

  &:focus-visible {
    outline: 2px solid #47a447;
    outline-offset: 1px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  margin-bottom: 18px;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #47a447, #398439);
  color: white;
  padding: 13px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  min-height: 48px;
  box-shadow: 0 4px 14px rgba(71, 164, 71, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.04);
    box-shadow: 0 6px 16px rgba(71, 164, 71, 0.4);
  }

  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }
`;

const FooterNote = styled.p`
  margin-top: 18px;
  font-size: 0.85rem;
  color: #5f7167;
  text-align: center;
`;

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </svg>
);

// Map Firebase / API errors to friendly messages
const getFriendlyError = (error) => {
  const code = error?.code || "";
  switch (code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact the organization.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return "Incorrect email or password. Please try again.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please wait a moment and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      break;
  }

  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return "You are not authorized to access the Hifz portal.";
  }
  if (error?.request && !error?.response) {
    return "Could not reach the server. Please try again shortly.";
  }
  return "Something went wrong while signing in. Please try again.";
};

const Login = ({ onLogin }) => {
  const apiURL = process.env.REACT_APP_API_URL;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // If already signed in, skip the login screen
  useEffect(() => {
    const existing = getEffectiveHifzUser();
    if (existing?.role) {
      navigate(dashboardPathForRole(existing.role), { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError("Please enter both your email and password.");
      return;
    }

    setLoading(true);
    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, password);
      const user = userCredential.user;
      const token = await user.getIdToken(true);

      const response = await axios.post(`${apiURL}/hifz-login`, { token });

      if (response.status === 200) {
        const { role, userId, email: userEmail } = response.data;

        localStorage.setItem("token", token);
        localStorage.setItem("role", role);
        localStorage.setItem("userId", userId);
        if (userEmail) localStorage.setItem("email", userEmail);

        onLogin({ token, role, userId, email: userEmail || "" });

        navigate(dashboardPathForRole(role), { replace: true });
      } else {
        setError("Unexpected response from the server. Please try again.");
      }
    } catch (err) {
      console.error("Error during authentication:", err);
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavbarAll />
      <Background>
        <LoginContainer>
          <LoginHeading>Hifz Login</LoginHeading>
          <SubHeading>Sign in to access the Hifz portal</SubHeading>

          {error && (
            <ErrorBanner role="alert">
              <AlertIcon />
              <span>{error}</span>
            </ErrorBanner>
          )}

          <LoginForm onSubmit={handleLogin} noValidate>
            <InputLabel htmlFor="hifz-email">Email</InputLabel>
            <InputField
              id="hifz-email"
              type="email"
              autoComplete="email"
              placeholder="you@zakariamasjid.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              $hasError={!!error}
              required
            />

            <InputLabel htmlFor="hifz-password">Password</InputLabel>
            <PasswordWrap>
              <InputField
                id="hifz-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                $hasError={!!error}
                required
              />
              <EyeButton
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                tabIndex={0}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </EyeButton>
            </PasswordWrap>

            <SubmitButton type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner aria-hidden="true" />
                  Signing in…
                </>
              ) : (
                "Login"
              )}
            </SubmitButton>
          </LoginForm>
          <FooterNote>* Contact the organization for login details</FooterNote>
        </LoginContainer>
      </Background>
    </>
  );
};

export default Login;
